import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Check, Edit2, Trash2, X, Save } from 'lucide-react';
import AgendaDiaryCard, { fileToDataUrl } from '../components/AgendaDiaryCard';
import AgendaProgressBar from '../components/AgendaProgressBar';
import { Sidebar } from '../components/Sidebar';

const AZUL = '#1E3A8A';
const FUNDO = '#EEF2FF';
const BORDA = '#DBEAFE';

const STATUS_LABEL = {
  pendente:     { label: 'Pendente',     bg: FUNDO,      color: AZUL },
  em_andamento: { label: 'Em andamento', bg: '#dbeafe',  color: '#1d4ed8' },
  concluida:    { label: 'Concluída',    bg: '#dcfce7',  color: '#15803d' },
  atrasada:     { label: 'Atrasada',     bg: '#fee2e2',  color: '#dc2626' },
  cancelada:    { label: 'Cancelada',    bg: '#f3f4f6',  color: '#6b7280' },
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };

const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const fmtDateTime = (s) => { if (!s) return '—'; return new Date(s).toLocaleString('pt-BR'); };

const inp = `w-full rounded-lg px-3 py-2 text-sm text-black focus:outline-none`;
const inpStyle = { backgroundColor: FUNDO, border: `1px solid ${BORDA}` };

export default function AgendaAtividadeDetalhe({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [diary, setDiary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);

  const loadActivity = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/agenda/activities/${id}`);
      setActivity(res.data);
    } catch {
      toast.error('Atividade não encontrada');
      navigate('/agenda');
    }
  }, [id, navigate]);

  const loadDiary = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/agenda/activities/${id}/diary`, { params: { limit: 7 } });
      setDiary(res.data);
      setHasMore(res.data.length >= 7);
    } catch {
      toast.error('Erro ao carregar diário');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadActivity(), loadDiary()]).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const loadMore = async () => {
    if (!diary.length) return;
    setLoadingMore(true);
    try {
      const before = diary[0].data_referencia;
      const res = await axiosInstance.get(`/agenda/activities/${id}/diary`, { params: { limit: 7, before } });
      setDiary(prev => [...res.data, ...prev]);
      setHasMore(res.data.length >= 7);
    } catch {
      toast.error('Erro ao carregar mais registros');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaveDiary = async (dataRef, conteudo) => {
    try {
      const res = await axiosInstance.put(`/agenda/activities/${id}/diary/${dataRef}`, { conteudo });
      setDiary(prev => prev.map(e => e.data_referencia === dataRef ? res.data : e));
      toast.success('Registro salvo!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar registro');
      throw err;
    }
  };

  const handleUpload = async (dataRef, files) => {
    try {
      const attachments = await Promise.all(files.map(async f => ({
        data_url: await fileToDataUrl(f), nome_original: f.name, tipo_mime: f.type,
      })));
      const res = await axiosInstance.post(`/agenda/activities/${id}/diary/${dataRef}/attachments`, attachments);
      setDiary(prev => prev.map(e => e.data_referencia === dataRef ? res.data : e));
      toast.success('Anexo(s) adicionado(s)!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar anexo');
      throw err;
    }
  };

  const handleComplete = async () => {
    try {
      await axiosInstance.put(`/agenda/activities/${id}/complete`);
      toast.success('Atividade concluída!');
      setShowConfirmComplete(false);
      loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao concluir atividade');
    }
  };

  const handleProgressChange = async (value) => {
    try {
      const res = await axiosInstance.put(`/agenda/activities/${id}`, { progresso: value });
      setActivity(res.data);
      toast.success('Progresso atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar progresso');
    }
  };

  const handleReopen = async () => {
    try {
      await axiosInstance.put(`/agenda/activities/${id}/reabrir`);
      toast.success('Atividade reaberta!');
      loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reabrir atividade');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta atividade? Esta ação não pode ser desfeita.')) return;
    try {
      await axiosInstance.delete(`/agenda/activities/${id}`);
      toast.success('Atividade excluída');
      navigate('/agenda');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir atividade');
    }
  };

  const openEdit = () => {
    setEditForm({
      titulo: activity.titulo, descricao: activity.descricao,
      data_inicio: activity.data_inicio, data_conclusao_prevista: activity.data_conclusao_prevista,
      prioridade: activity.prioridade, visibilidade: activity.visibilidade,
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/agenda/activities/${id}`, editForm);
      setActivity(res.data);
      toast.success('Atividade atualizada!');
      setShowEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar atividade');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !activity) {
    return (
      <div className="flex min-h-screen bg-zinc-950">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: FUNDO }}>
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: AZUL, borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  const sc = STATUS_LABEL[activity.status] || STATUS_LABEL.pendente;
  const editableDiary = activity.status !== 'concluida';

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 p-8 overflow-x-hidden" style={{ backgroundColor: FUNDO }}>
    <div className="animate-fade-in space-y-6 pb-10 max-w-3xl">
      <button
        onClick={() => navigate('/agenda')}
        className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 text-white"
        style={{ backgroundColor: '#f97316' }}
      >
        <ArrowLeft size={15} /> Voltar para Agenda
      </button>

      <div className="rounded-xl p-5 space-y-4 bg-white" style={{ border: `1px solid ${BORDA}` }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-black">{activity.titulo}</h1>
              <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
              <span className="text-xs px-2 py-1 rounded-full text-black" style={{ backgroundColor: BORDA }}>{PRIORIDADE_LABEL[activity.prioridade]}</span>
            </div>
            <p className="text-sm text-black mt-2 whitespace-pre-wrap" style={{ opacity: 0.7 }}>{activity.descricao}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            {activity.status !== 'concluida' && (
              <button
                onClick={() => setShowConfirmComplete(true)}
                className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#15803d' }}
              >
                <Check size={14} /> Concluir
              </button>
            )}
            {activity.status === 'concluida' && (
              <button
                onClick={handleReopen}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-80"
                style={{ backgroundColor: BORDA, color: AZUL }}
              >
                Reabrir
              </button>
            )}
            {activity.status !== 'concluida' && (
              <button onClick={openEdit} className="p-2 rounded-lg transition-colors hover:opacity-70" style={{ color: AZUL }}>
                <Edit2 size={15} />
              </button>
            )}
            <button onClick={handleDelete} className="p-2 rounded-lg transition-colors hover:bg-red-50" style={{ color: '#dc2626', opacity: 0.6 }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm pt-4" style={{ borderTop: `1px solid ${BORDA}` }}>
          <div><p className="text-xs" style={{ color: AZUL, opacity: 0.5 }}>Início</p><p className="text-black">{fmtDate(activity.data_inicio)}</p></div>
          <div><p className="text-xs" style={{ color: AZUL, opacity: 0.5 }}>Conclusão Prevista</p><p className="text-black">{fmtDate(activity.data_conclusao_prevista)}</p></div>
          <div><p className="text-xs" style={{ color: AZUL, opacity: 0.5 }}>Conclusão Real</p><p className="text-black">{activity.data_conclusao_real ? fmtDateTime(activity.data_conclusao_real) : '—'}</p></div>
          <div className="col-span-2 sm:col-span-3">
            <p className="text-xs" style={{ color: AZUL, opacity: 0.5 }}>Responsável(is)</p>
            <p className="text-black">{activity.responsaveis.map(r => r.nome).join(', ') || '—'}</p>
          </div>
        </div>

        <div className="pt-4" style={{ borderTop: `1px solid ${BORDA}` }}>
          <p className="text-xs mb-2" style={{ color: AZUL, opacity: 0.5 }}>Progresso</p>
          <AgendaProgressBar
            value={activity.progresso || 0}
            editable={activity.status !== 'concluida'}
            onChange={handleProgressChange}
          />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: AZUL }}>Diário de Atividades</h2>
        {hasMore && (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="text-xs disabled:opacity-50 transition-opacity hover:opacity-70"
            style={{ color: AZUL }}
          >
            {loadingMore ? 'Carregando...' : 'Carregar registros anteriores'}
          </button>
        )}
        {diary.length === 0 ? (
          <p className="text-sm" style={{ color: AZUL, opacity: 0.4 }}>Nenhum registro de diário ainda.</p>
        ) : (
          <div className="space-y-3">
            {diary.map(entry => (
              <AgendaDiaryCard key={entry.id} entry={entry} editable={editableDiary} onSave={handleSaveDiary} onUpload={handleUpload} />
            ))}
          </div>
        )}
      </div>

      {showConfirmComplete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="rounded-2xl p-6 w-full max-w-sm shadow-2xl bg-white" style={{ border: `1px solid ${BORDA}` }}>
            <h2 className="text-lg font-bold text-black mb-2">Concluir atividade?</h2>
            <p className="text-sm text-black mb-3" style={{ opacity: 0.7 }}>
              Você tem certeza que concluiu esse compromisso? Se quiser apenas salvar o diário do dia, use o botão <strong>Salvar registro</strong> no diário abaixo.
            </p>
            <p className="text-xs mb-5" style={{ color: AZUL, opacity: 0.5 }}>As linhas de diário futuras não serão mais geradas.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmComplete(false)}
                className="px-4 py-2 text-sm rounded-lg transition-colors text-black"
                style={{ border: `1px solid ${BORDA}` }}
              >
                Cancelar
              </button>
              <button
                onClick={handleComplete}
                className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#15803d' }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && editForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="rounded-2xl p-6 w-full max-w-lg shadow-2xl my-8 bg-white" style={{ border: `1px solid ${BORDA}` }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold" style={{ color: AZUL }}>Editar Atividade</h2>
              <button onClick={() => setShowEdit(false)} style={{ color: AZUL, opacity: 0.5 }}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs mb-1 block text-black">Título</label>
                <input className={inp} style={inpStyle} value={editForm.titulo} onChange={e => setEditForm({ ...editForm, titulo: e.target.value })} />
              </div>
              <div>
                <label className="text-xs mb-1 block text-black">Descrição</label>
                <textarea
                  className={`${inp} min-h-[80px] resize-y`} style={inpStyle}
                  value={editForm.descricao} onChange={e => setEditForm({ ...editForm, descricao: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block text-black">Data de Início</label>
                  <input type="date" className={inp} style={inpStyle} value={editForm.data_inicio} onChange={e => setEditForm({ ...editForm, data_inicio: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs mb-1 block text-black">Data de Conclusão</label>
                  <input type="date" className={inp} style={inpStyle} value={editForm.data_conclusao_prevista} onChange={e => setEditForm({ ...editForm, data_conclusao_prevista: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs mb-1 block text-black">Prioridade</label>
                  <select className={inp} style={inpStyle} value={editForm.prioridade} onChange={e => setEditForm({ ...editForm, prioridade: e.target.value })}>
                    {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs mb-1 block text-black">Visibilidade</label>
                  <select className={inp} style={inpStyle} value={editForm.visibilidade} onChange={e => setEditForm({ ...editForm, visibilidade: e.target.value })}>
                    <option value="privada">Privada</option>
                    <option value="equipe">Equipe</option>
                    <option value="publica">Pública</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowEdit(false)} className="px-4 py-2 text-sm rounded-lg transition-colors text-black" style={{ border: `1px solid ${BORDA}` }}>
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-white rounded-lg font-medium text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: AZUL }}
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      </div>
    </div>
  );
}
